import React, { Dispatch, SetStateAction, useState } from 'react'
import { Wrapper } from '@googlemaps/react-wrapper'
import clsx from 'clsx'
import { Map } from '../../organism/Map/Map'
import { Marker } from '../../primitive/Marker/Marker'
import { Search } from '../Search/Search'
import styles from './MapWrapper.module.scss'
import { MapLocation } from '../../primitive/MapLocation/MapLocation'
import { MapZoom } from '../../primitive/MapZoom/MapZoom'
import { ResultListToggler } from '../../primitive/ResultListToggler/ResultListToggler'
import { FilterConfigProps, Filter } from '../Filter/Filter'
import { useFetchData } from './hooks'
import { ConfigProps, StationObjProps } from '../../page/Map'
import { Updater } from 'use-immer'
import { AppStateType } from '../../../type/AppStateType'
import { PositionType } from '../../../type/PositionType'
import { MapLinks } from '../../organism/MapLinks/MapLinks'
import { MapLinksControls } from '../../primitive/MapLinksControls/MapLinksControls'
import {
    DEFAULT_LAT,
    DEFAULT_LNG,
    getCurrentPosition,
} from '../../../utils/geolocation'
import { handleGeocoder } from '../../../utils/handleGeocoder'

const key = window.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
const mapId = window.env.REACT_APP_GOOGLE_MAP_ID || ''
const defaultZoom = Number(window.env.REACT_APP_GOOGLE_MAP_DEFAULT_ZOOM) || 18
const wasteCollectionZoom =
    Number(window.env.REACT_APP_WASTE_COLLECTION_GOOGLE_MAP_DEFAULT_ZOOM) || 12

// lat and lng for default position – fallback to Náměstí Míru if env not set
const defaultLat =
    Number(window.env.REACT_APP_DEFAULT_CENTER_LAT) || DEFAULT_LAT
const defaultLng =
    Number(window.env.REACT_APP_DEFAULT_CENTER_LNG) || DEFAULT_LNG

interface MapWrapperProps {
    config: ConfigProps
    updateConfig: Updater<ConfigProps>
    stationsObj: StationObjProps
    updateStationsObj: Updater<StationObjProps>
    setShowSpinner: Dispatch<SetStateAction<boolean>>
    map?: google.maps.Map
    setMap: Dispatch<SetStateAction<google.maps.Map | undefined>>
}

export const MapWrapper = ({
    config,
    updateConfig,
    setShowSpinner,
    stationsObj,
    updateStationsObj,
    map,
    setMap,
}: MapWrapperProps) => {
    // geolocation contains current user position if it is allowed
    const [geolocation, setGeolocation] = useState<PositionType>(undefined)
    // filter config
    const [filterConfig, setFilterConfig] = useState<FilterConfigProps>({
        trashTypes: [],
        onlyMonitored: false,
    })
    // handle state of map links
    const [isMapLinksOpen, setIsMapLinksOpen] = useState<boolean>(true)
    const zoom =
        config.type === 'waste-collection' ? wasteCollectionZoom : defaultZoom

    const updatePosition = (position: PositionType) => {
        updateConfig((draft) => {
            draft.position = position
        })
    }

    // hook on init to get user's position as early as possible
    React.useEffect(() => {
        // Set default position immediately so the map is never invisible
        updatePosition({ lat: defaultLat, lng: defaultLng })

        let watchId: number | undefined

        const startWatcher = () => {
            if (watchId !== undefined) return
            if ('geolocation' in navigator) {
                watchId = navigator.geolocation.watchPosition(
                    ({ coords: { latitude: lat, longitude: lng } }) => {
                        setGeolocation({ lat, lng })
                    },
                    (e) => {
                        console.log(`Watch error ${e.code}: ${e.message}`)
                    },
                    {
                        enableHighAccuracy: false,
                        timeout: 10000,
                        maximumAge: 30000,
                    }
                )
            }
        }

        // Fetch location to possibly trigger browser permission prompt on load
        getCurrentPosition({
            onSuccess: ({ coords: { latitude: lat, longitude: lng } }) => {
                // Once granted/fetched successfully, keep monitoring position with watcher
                startWatcher()

                // Init map center if google maps is loaded and we are in Praha
                if (
                    typeof google !== 'undefined' &&
                    google.maps &&
                    google.maps.Geocoder
                ) {
                    handleGeocoder(
                        lat,
                        lng,
                        defaultLat,
                        defaultLng,
                        updatePosition,
                        setGeolocation
                    )
                } else {
                    // Google maps scripts not finished parsing yet
                    // Only display user dot, don't move map away from default yet
                    setGeolocation({ lat, lng })
                }
            },
            onError: (e) => {
                console.log(`Init geolocation error ${e.code}: ${e.message}`)
                // Start watcher anyway, maybe they enable it later via settings?
                // actually, if denied, watcher will just fail with permission denied too.
            },
        })

        return () => {
            if (watchId !== undefined) navigator.geolocation.clearWatch(watchId)
        }
    }, [])

    const setStations = (stations: StationObjProps['stations']) => {
        updateStationsObj((draft) => {
            draft.stations = stations
        })
    }

    // fetch data from API
    useFetchData({
        setStations,
        setShowSpinner,
        setState: (state: AppStateType) => {
            updateConfig((draft) => {
                draft.state = state
            })
        },
        position: config.position,
        filterConfig,
        map,
        zoom,
        type: config.type,
    })

    // update position helper moved up

    // update active station helper
    const updateActiveStation = (station: StationObjProps['activeStation']) => {
        updateStationsObj((draft) => {
            draft.activeStation = station
        })
    }

    if (!config.type) return <></>

    return (
        <div
            className={clsx(
                styles.wrapper,
                !config.position && styles.invisible
            )}
        >
            <Wrapper apiKey={key} libraries={['places', 'geometry']}>
                <Map
                    disableDefaultUI={true}
                    map={map}
                    zoom={zoom}
                    position={config.position}
                    stations={stationsObj.stations}
                    mapId={mapId}
                    activeStation={stationsObj.activeStation}
                    setMap={setMap}
                    setActiveStation={updateActiveStation}
                    mapType={config.type}
                >
                    {map && geolocation && (
                        <Marker
                            icon={{
                                // 45x45 size
                                url: `/img/layout/map-marker-user-position.svg`,
                                size: new google.maps.Size(45, 45),
                                origin: new google.maps.Point(0, 0),
                                anchor: new google.maps.Point(22.5, 22.5),
                            }}
                            position={geolocation}
                            zIndex={999999999999}
                        />
                    )}
                    {map && config.position && (
                        <Marker
                            icon={{
                                // 42x56 size
                                url: `/img/layout/map-marker-searched-address.svg`,
                                size: new google.maps.Size(42, 56),
                                origin: new google.maps.Point(0, 0),
                                anchor: new google.maps.Point(21, 56),
                            }}
                            position={config.position}
                            zIndex={999999999998}
                        />
                    )}
                </Map>
                <Search setPosition={updatePosition} />
                <div
                    className={clsx(
                        styles.controls,
                        (config.resultListOpen || isMapLinksOpen) &&
                            styles.controlsOpen
                    )}
                >
                    <div className={styles.controlsItem}>
                        <MapLocation
                            setPosition={updatePosition}
                            setGeolocation={setGeolocation}
                            geolocation={geolocation}
                        />
                        <MapZoom map={map} />
                        <MapLinksControls
                            open={isMapLinksOpen}
                            setOpen={setIsMapLinksOpen}
                        />
                        <ResultListToggler
                            className={styles.controlList}
                            config={config}
                            updateConfig={updateConfig}
                        />
                    </div>
                </div>
                <MapLinks
                    setShowSpinner={setShowSpinner}
                    open={isMapLinksOpen}
                    setOpen={setIsMapLinksOpen}
                    className={clsx(
                        styles.controlsLinks,
                        isMapLinksOpen && styles.controlsLinksOpen
                    )}
                />
                {(config.type === 'sorted-waste' ||
                    config.type === 'bulky-waste') && (
                    <Filter
                        filterConfig={filterConfig}
                        setFilterConfig={setFilterConfig}
                        updateConfig={updateConfig}
                        config={config}
                        className={styles.filter}
                        key={`filter-${config.type}`}
                    />
                )}
            </Wrapper>
        </div>
    )
}
